// Klickbare Uebernahme-Texte neben den Feldtiteln im Aufschalt-Formular:
//   „xx,xx € übernehmen"      am Verkaufspreis  (Feld listing_price)
//   „N verfügbar übernehmen"  am Lagerbestand   (Feld amount)
//
// Preis: aus der Edge Function kaufland-price-suggestion, der aktuelle Stammdaten-Bruttopreis
// des Vario-Artikels, fuer CZ/PL mit Varios Tageskurs in die Storefront-Waehrung umgerechnet.
// Er wird NICHT still ins Feld geschrieben (der Stammdatenpreis ist nicht der Preis dieses
// Verkaufskanals, Entscheidung vom 2026-08-17), sondern erst auf Klick des Haendlers.
//
// Bestand: `meta.autoStockValue` aus vario-mapping-sources, also die Summe von
// `stockAvailable` ueber genau die Lager, die in den Kaufland-Einstellungen der Storefront
// zugeordnet sind (`account.kaufland_storefronts.vario_storage_ids`). Dieselbe Kaskade
// speist Vorschau und Bestands-Push; der Klicktext zeigt also den Wert, der auch gepusht
// wuerde.
//
// Netzfrei und bis auf zwei duenne Leser DOM-frei, wie offer-conditions-form.ts: Formatierung
// und Klick-Handler laufen unter `vitest` mit `environment: 'node'`.

// ---------------------------------------------------------------------------
// Gemeinsamer Kern
// ---------------------------------------------------------------------------

/** Attribute am Text; Klassen liegen in bridge.css (Abschnitt Aufschalt-Formular). */
export const TAKEOVER_FIELD_ATTR = 'data-takeover-field';
export const TAKEOVER_VALUE_ATTR = 'data-takeover-value';

export interface TakeoverText {
  /** `name` des Ziel-Inputs im Formular, z. B. `listing_price` oder `amount`. */
  field: string;
  /** Roher Wert fuers Input (Punkt als Dezimaltrenner). */
  value: string;
  /** Sichtbarer Klicktext. */
  label: string;
  title?: string;
}

/**
 * Markup neben dem Feldtitel: der klickbare Text, oder ohne Wert ein stummer Hinweis, oder
 * nichts.
 *
 * Bewusst KEIN <button>: Vorgabe des Betreibers (2026-09-12) ist Text in Vario-Gruen. Ein
 * <span role="button" tabindex="0"> bleibt per Tastatur erreichbar; Enter und Leertaste
 * behandelt wireTakeover wie den Klick.
 */
export function renderTakeover(t: TakeoverText | null, opts: { hinweis?: string } = {}): string {
  if (t) {
    const title = t.title ? ` title="${escapeHtml(t.title)}"` : '';
    return `<span class="klst-attach-takeover" role="button" tabindex="0" `
      + `${TAKEOVER_FIELD_ATTR}="${escapeHtml(t.field)}" ${TAKEOVER_VALUE_ATTR}="${escapeHtml(t.value)}"${title}>`
      + `${escapeHtml(t.label)}</span>`;
  }
  if (opts.hinweis) {
    return `<span class="klst-attach-takeover-hint">${escapeHtml(opts.hinweis)}</span>`;
  }
  return '';
}

/** Minimalschnitt statt DOM-Typen, damit der Handler ohne Browser testbar bleibt. */
interface ClickTarget {
  closest(selector: string): unknown;
}
interface FormLike {
  querySelector(selector: string): unknown;
}
interface InputLike {
  value: string;
  focus(): void;
  dispatchEvent(ev: Event): unknown;
}

/**
 * Traegt den Wert des geklickten Texts ins zugehoerige Feld ein. Liefert true, wenn ein
 * Uebernehmen-Text getroffen wurde. Das `input`-Event macht die Aenderung fuer alles
 * sichtbar, was am Feld lauscht, so wie eine Tastatureingabe. Der Feldname wird geprueft,
 * bevor er in einen Selektor wandert.
 */
export function handleTakeoverClick(target: ClickTarget, form: FormLike): boolean {
  const el = target.closest(`[${TAKEOVER_FIELD_ATTR}]`) as { getAttribute(name: string): string | null } | null;
  if (!el) return false;
  const field = el.getAttribute(TAKEOVER_FIELD_ATTR);
  const value = el.getAttribute(TAKEOVER_VALUE_ATTR);
  if (!field || !/^[a-z_]+$/.test(field) || value === null) return false;
  const input = form.querySelector(`input[name="${field}"]`) as InputLike | null;
  if (!input) return false;
  input.value = value;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();
  return true;
}

/**
 * Ein delegierter Handler pro Formular fuer alle Uebernahme-Texte. Weil die Elemente keine
 * <button> sind, loesen Enter und Leertaste nicht von selbst aus, deshalb der keydown-Zweig
 * (Leertaste ohne preventDefault wuerde das Formular scrollen).
 */
export function wireTakeover(form: HTMLFormElement): void {
  form.addEventListener('click', (ev) => {
    const t = ev.target;
    if (t instanceof Element) handleTakeoverClick(t, form);
  });
  form.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const t = ev.target;
    if (!(t instanceof Element) || !t.closest(`[${TAKEOVER_FIELD_ATTR}]`)) return;
    ev.preventDefault();
    handleTakeoverClick(t, form);
  });
}

// ---------------------------------------------------------------------------
// Verkaufspreis
// ---------------------------------------------------------------------------

export interface PriceSuggestion {
  /** Betrag in ganzen Cent (bzw. Haleru/Grosze) der Waehrung. */
  priceCents: number;
  /** ISO-4217, drei Grossbuchstaben. */
  currency: string;
}

/** Deutsch formatierter Betrag: `19,90 €`, `241,00 CZK`. Wirft nie. */
export function formatPreis(cents: number, currency: string): string {
  const code = /^[A-Z]{3}$/.test(currency) ? currency : 'EUR';
  const betrag = cents / 100;
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).format(betrag);
  } catch {
    return `${betrag.toFixed(2).replace('.', ',')} ${code}`;
  }
}

export function takeoverLabel(s: PriceSuggestion): string {
  return `${formatPreis(s.priceCents, s.currency)} übernehmen`;
}

/** Wert fuer das `<input type="number">`: Punkt als Dezimaltrenner, zwei Nachkommastellen. */
export function takeoverInputValue(s: PriceSuggestion): string {
  return (s.priceCents / 100).toFixed(2);
}

/** Antwort der Edge Function kaufland-price-suggestion, soweit das Frontend sie liest. */
export interface PriceSuggestionResp {
  suggestion: PriceSuggestion | null;
  /** `ok`, `no-article-price`, `no-rate`, `storefront-unknown` */
  reason: string;
  rate: { faktor: number; validFrom: string; richtung: string } | null;
  /** Gesetzt, wenn die Kursabfrage selbst scheiterte (kein „kein Kurs", sondern ein Fehler). */
  rateError?: string;
  storefront: { code: string; currency: string | null };
  article: { grossPriceCents: number | null; currency: string };
}

/** Was neben dem Feldtitel steht: Klicktext (mit Tooltip) oder stummer Hinweis oder nichts. */
export interface PreisvorschlagAnzeige {
  suggestion: PriceSuggestion | null;
  title?: string;
  hinweis?: string;
}

/**
 * Uebersetzt die Function-Antwort in Anzeige. Ein fehlender Stammdatenpreis ist kein Fehler
 * und bekommt keinen Hinweis; ein fehlender Kurs schon, weil der Haendler ihn beheben kann
 * (Waehrung in Vario anlegen, Vario zieht den Kurs dann selbst).
 */
export function beschreibePreisvorschlag(r: PriceSuggestionResp): PreisvorschlagAnzeige {
  const waehrung = r.storefront.currency ?? '';
  if (r.suggestion) {
    const umgerechnet = r.rate && r.suggestion.currency !== r.article.currency;
    if (umgerechnet && r.rate) {
      const stamm = r.article.grossPriceCents !== null ? formatPreis(r.article.grossPriceCents, r.article.currency) : '';
      const faktor = r.rate.faktor.toLocaleString('de-DE', { maximumFractionDigits: 4 });
      return {
        suggestion: r.suggestion,
        title: `Aktueller Verkaufspreis ${stamm} (brutto) aus den Vario-Stammdaten, umgerechnet mit Vario-Kurs `
          + `1 ${r.article.currency} = ${faktor} ${r.suggestion.currency} vom ${formatDatum(r.rate.validFrom)}`,
      };
    }
    return { suggestion: r.suggestion, title: 'Aktueller Verkaufspreis (brutto) aus den Vario-Stammdaten des Artikels' };
  }
  if (r.rateError) {
    return { suggestion: null, hinweis: `Kursabfrage in Vario fehlgeschlagen, kein Vorschlag in ${waehrung}` };
  }
  if (r.reason === 'no-rate') {
    return { suggestion: null, hinweis: `Kein ${waehrung}-Kurs in Vario hinterlegt (Währung in Vario anlegen, den Kurs zieht Vario täglich selbst)` };
  }
  return { suggestion: null };
}

export function renderPriceTakeover(
  s: PriceSuggestion | null,
  opts: { title?: string; hinweis?: string } = {},
): string {
  const text: TakeoverText | null = s
    ? { field: 'listing_price', value: takeoverInputValue(s), label: takeoverLabel(s), ...(opts.title ? { title: opts.title } : {}) }
    : null;
  return renderTakeover(text, opts.hinweis ? { hinweis: opts.hinweis } : {});
}

function formatDatum(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

// ---------------------------------------------------------------------------
// Lagerbestand
// ---------------------------------------------------------------------------

export function bestandLabel(n: number): string {
  return `${n} verfügbar übernehmen`;
}

/** Bestandskaskade aus vario-mapping-sources (`meta.autoStock*`), soweit hier gelesen. */
export interface BestandsQuelle {
  value: number | null;
  /** `ok`, `no-storage-mapped`, `storage-lookup-failed`, `storage-fetch-failed`, `no-stock-data`, `no-article` */
  reason: string | null;
  storageLabel: string | null;
  storageCount: number;
}

export interface BestandsvorschlagAnzeige {
  value: number | null;
  title?: string;
  hinweis?: string;
}

/**
 * Uebersetzt die Bestandskaskade in Anzeige. Ein fehlendes Lager in den Einstellungen und
 * ein Vario-Fehler bekommen einen Hinweis, weil der Haendler beides beheben oder melden
 * kann; ein unbekannter Artikel nicht.
 */
export function beschreibeBestandsvorschlag(q: BestandsQuelle): BestandsvorschlagAnzeige {
  if (q.value !== null && Number.isFinite(q.value)) {
    const value = Math.max(0, Math.floor(q.value));
    const quelle = q.storageCount === 1 && q.storageLabel
      ? `aus dem Lager „${q.storageLabel}"`
      : `als Summe über ${q.storageCount} Lager`;
    return {
      value,
      title: `Verfügbarer Bestand aus Vario, ${quelle}, so wie in den Kaufland-Einstellungen der Storefront zugeordnet`,
    };
  }
  switch (q.reason) {
    case 'no-storage-mapped':
      return { value: null, hinweis: 'Kein Vario-Lager in den Kaufland-Einstellungen dieser Storefront zugeordnet' };
    case 'storage-lookup-failed':
    case 'storage-fetch-failed':
      return { value: null, hinweis: 'Bestand aus Vario gerade nicht ladbar' };
    case 'no-stock-data':
      return { value: null, hinweis: 'Vario liefert für die zugeordneten Lager keinen Bestand' };
    default:
      return { value: null };
  }
}

export function renderStockTakeover(
  value: number | null,
  opts: { title?: string; hinweis?: string } = {},
): string {
  const text: TakeoverText | null = value !== null
    ? { field: 'amount', value: String(value), label: bestandLabel(value), ...(opts.title ? { title: opts.title } : {}) }
    : null;
  return renderTakeover(text, opts.hinweis ? { hinweis: opts.hinweis } : {});
}

/** Eigene Kopie, aus demselben Grund wie in offer-conditions-form.ts: kein Import-Zyklus. */
function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
